import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { QuoteModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/quote/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/quote/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/quote/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/quote/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/quote/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/quote/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/quote/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/quote/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/quote/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/quote/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/quote/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/quote/DELETE_FAILURE'

export type QuoteModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: QuoteModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadQuotes(options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/quotes.json', options)
      .then(({ data }: { data: { entities: QuoteModel[] } }) => {
        const normalizedJson = normalize(data.entities, Schemas.QUOTE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadQuote(quoteId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.quotes.loadedIds]

    if (quoteId && !loadedIds.includes(quoteId)){
      loadedIds.push(quoteId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/quotes/${quoteId}.json`, options)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createQuote(quote: Partial<QuoteModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    body: JSON.stringify({
      quote,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/quotes.json', options, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateQuote(
  quote: UpdateParams<QuoteModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      quote,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/quotes/${quote.id}.json`, options, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateQuoteSectionItemOrder(
  quote: QuoteModel,
  sortedSectionItems: number[],
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      sorted_section_items: sortedSectionItems,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/quotes/${quote.id}/update_section_item_sort.json`, options, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateQuoteTotal(quoteId: number, total: number) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { quotes } = state.entities

    const quote = quotes[quoteId]

    if (quote){
      const updatedQuote = { ...quote, total }

      const normalizedJson = normalize(updatedQuote, Schemas.QUOTE)
      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else {
      dispatch(updateFailure(['Quote does not exist.']))
    }
  }
}

export function saveQuoteAsTemplate(
  quoteTemplate: Partial<QuoteModel>,
  existingQuote: QuoteModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    body: JSON.stringify({
      quote: quoteTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/quotes/${existingQuote.id}/save_as_template.json`, options, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteQuote(quote: DeleteParams<QuoteModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/quotes/${quote.id}.json`, {}, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadFromExternalPlatform(externalPlatformKey: string, options: EntityOptions = {}) {
  return () => {
    const promise = api(`/quotes/fetch_from_external_platform/${externalPlatformKey}.json`, options)
      .then(({ data }) => ({ success: true as const, data: data.data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

export function importFromExternalPlatform(
  externalPlatformKey: string,
  payload: {
    externalId: number,
    organizationId: number,
    projectId: number,
  },
  options: EntityOptions = {},
) {
  const { externalId, organizationId, projectId } = payload || {}

  const config = {
    method: 'POST',
    body: JSON.stringify({
      external_id: externalId,
      organization_id: organizationId,
      project_id: projectId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/quotes/import_from_external_platform/${externalPlatformKey}.json`, options, config)
      .then(({ data }: { data: { entity: QuoteModel } }) => {
        const normalizedJson = normalize(data.entity, Schemas.QUOTE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Loads the url to manage all quotes in platform. This is needed when no quotes have been created yet.

export function loadExternalPlatformManagementUrl(externalPlatformKey: string, options: EntityOptions = {}) {
  return () => {
    const promise = api(`/quotes/external_platform_management_url/${externalPlatformKey}.json`, options)
      .then(({ data }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}
// Loads the url to manage an individual quote in the platform.

export function loadExternalPlatformManagementUrlForQuoteId(quoteId: number, options: EntityOptions = {}) {
  return () => {
    const promise = api(`/quotes/${quoteId}/external_platform_management_url.json`, options)
      .then(({ data }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadExternalPlatformPdfUrl(quoteId: number, options: EntityOptions = {}) {
  return () => {
    const promise = api(`/quotes/${quoteId}/external_platform_pdf_url.json`, options)
      .then(({ data }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: QuoteModuleState = initialState,
  action: Action = { type: '' },
): QuoteModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
